import type { ApiRequest, ApiResponse } from "@trip/contracts/bridge";
import type { PaletteColor } from "@trip/domain/palette";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, detail: string) {
    super(detail);
    this.status = status;
    this.code = code;
  }
}

/**
 * Where the API lives. Empty (the default) means the same origin that served this page. For split
 * hosting, build with VITE_API_BASE=https://api.example.dev: requests then go cross-origin, and the
 * browser sends the API's session cookie because both hosts are on the same site.
 */
export const API_BASE: string = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.["VITE_API_BASE"] ??
  ""
).replace(/\/$/, "");
const credentialsMode: RequestCredentials = API_BASE ? "include" : "same-origin";

let csrfToken: string | null = null;

export async function bootstrapSession(): Promise<{ userId: string } | null> {
  const res = await fetch(`${API_BASE}/v1/auth/session`, { credentials: credentialsMode });
  if (res.status === 401) return null;
  if (!res.ok) throw new ApiError(res.status, "UNAVAILABLE", "Could not reach the server.");
  const body = (await res.json()) as { userId: string; csrfToken: string };
  csrfToken = body.csrfToken;
  return { userId: body.userId };
}

export const loginUrl = (returnTo: string) =>
  `${API_BASE}/v1/auth/login?returnTo=${encodeURIComponent(returnTo)}`;

export async function logout(): Promise<void> {
  await send("POST", "/v1/auth/logout");
}

/**
 * How a request reaches the API. In a browser: `fetch` with the session cookie and CSRF header.
 * Inside the Android shell: the native bridge (`api.request`), which adds the bearer token itself,
 * so no token or cookie is ever visible to JavaScript.
 */
export type Transport = (req: ApiRequest) => Promise<ApiResponse>;

export const fetchTransport: Transport = async (req) => {
  const headers: Record<string, string> = {};
  if (req.body !== undefined) headers["content-type"] = "application/json";
  if (req.method !== "GET") headers["x-csrf-token"] = csrfToken ?? "";
  if (req.idempotencyKey) headers["idempotency-key"] = req.idempotencyKey;
  const res = await fetch(`${API_BASE}${req.path}`, {
    method: req.method,
    headers,
    credentials: credentialsMode,
    ...(req.body === undefined ? {} : { body: JSON.stringify(req.body) }),
  });
  if (res.status === 204) return { status: 204, body: undefined };
  return { status: res.status, body: await res.json().catch(() => ({})) };
};

let transport: Transport = fetchTransport;
/** Set once at start-up by the native shell's bootstrap (Phase 8); tests use it too. */
export function setTransport(t: Transport) {
  transport = t;
}

/** A request, answered with its status as well as its body; failures throw as `send`'s do. */
async function sendWithStatus<T>(
  method: ApiRequest["method"],
  path: string,
  body?: unknown,
  retryKey?: string,
): Promise<{ status: number; body: T }> {
  const req: ApiRequest = { method, path };
  if (body !== undefined) req.body = body;
  if (method === "POST") req.idempotencyKey = retryKey ?? crypto.randomUUID();
  const res = await transport(req);
  if (res.status === 401 && transport === fetchTransport) {
    window.location.assign(loginUrl(window.location.pathname + window.location.hash));
    throw new ApiError(401, "UNAUTHENTICATED", "Signing in…");
  }
  if (res.status === 204) return { status: 204, body: undefined as T };
  const data = (res.body ?? {}) as Record<string, unknown>;
  if (res.status < 200 || res.status >= 300) {
    throw new ApiError(
      res.status,
      String(data["code"] ?? "ERROR"),
      String(data["detail"] ?? `Request failed (${res.status})`),
    );
  }
  return { status: res.status, body: data as T };
}

async function send<T>(
  method: ApiRequest["method"],
  path: string,
  body?: unknown,
  retryKey?: string,
): Promise<T> {
  return (await sendWithStatus<T>(method, path, body, retryKey)).body;
}

export const get = <T>(path: string) => send<T>("GET", path);
export const post = <T>(path: string, body?: unknown, retryKey?: string) =>
  send<T>("POST", path, body ?? {}, retryKey);
/**
 * A POST that may find what it asked for already there: `created` is true for a 201 and false for
 * the 200 a server answers with the existing one (a category under a name the trip already has).
 */
export const postCreating = async <T>(path: string, body: unknown) => {
  const r = await sendWithStatus<T>("POST", path, body);
  return { created: r.status === 201, body: r.body };
};
export const put = <T>(path: string, body: unknown) => send<T>("PUT", path, body);
export const patch = <T>(path: string, body: unknown) => send<T>("PATCH", path, body);
export const del = <T>(path: string) => send<T>("DELETE", path);

// ---- DTOs (mirrors of the API's responses) ----------------------------------------------------
export type Me = {
  user: { id: string; displayName: string };
  memberships: { tripId: string; roles: string[] }[];
};
export type Trip = {
  id: string;
  name: string;
  destination: string | null;
  state: "DRAFT" | "SCHEDULED" | "ACTIVE" | "ENDED" | "ARCHIVED";
  captureStartAt: string | null;
  captureEndAt: string | null;
  windowVersion: number;
  mapLayersEnabled: boolean;
  roles: string[];
};
export type Component = {
  key: string;
  sizeBytes: string;
  mediaType: string;
  originalFidelity: string;
  availability: string;
};
export type Asset = {
  id: string;
  contributorId: string;
  contributorName: string | null;
  own: boolean;
  /**
   * The tags filed on it that this reader may see. A tag with a note on it is what the app
   * calls an idea. Somebody else's private folder never appears here — the server leaves it
   * out, so a badge can never give one away.
   */
  tags: { id: string; name: string; note: string | null; color: PaletteColor }[];
  /** The playable video proxy exists. */
  previewReady: boolean;
  /** The grid tile exists; a tile that will never render shows a placeholder instead. */
  thumbReady: boolean;
  kind: string;
  captureAt: string | null;
  /** When it reached the trip. What the sheet dates an undated recording by. */
  createdAt: string;
  /** How long a video runs; null for a photo, or until the probe has measured it. */
  durationMs: number | null;
  eligibility: string;
  sharingState: string;
  /** How many travellers have hearted it, and whether you are one of them. */
  likeCount: number;
  liked: boolean;
  /** Where it was taken, with how that was arrived at; null when nothing could place it. */
  location: { lat: number; lon: number; source: string; confidence: number } | null;
  /** The name of that place, once the worker has asked. Null while unasked, or if unnamed. */
  placeName: string | null;
  components: Component[];
};
/** Which local days hold recordings, for the library's day tabs. */
export type AssetDays = { items: { day: string; count: number }[]; undated: number };
/** Read URLs for a screenful of tiles; `missing` is what has no rendition (yet, or ever). */
export type ThumbGrants = {
  items: { assetId: string; url: string; expiresAt: string }[];
  missing: string[];
};
export type Deliverable = {
  id: string;
  title: string;
  status: string;
  currentRevisionId: string | null;
  /** The task this project answers, if any. */
  tagId: string | null;
  /** The task's owner, who may approve the cut made from their brief; null if none is visible. */
  approver: { userId: string; name: string } | null;
};
export type Revision = {
  id: string;
  versionNumber: number;
  sha256: string;
  notes: string | null;
  createdAt: string;
  createdBy: string;
  isCurrent: boolean;
  decisions: {
    id: string;
    reviewerId: string;
    decision: string;
    reason: string | null;
    at: string;
  }[];
};
export type Comment = {
  id: string;
  authorId: string;
  offsetMs: number | null;
  body: string;
  createdAt: string;
};
/** A task on the editor's board: a tag with a note. `projectId` is the live project answering it. */
export type BoardTask = {
  tagId: string;
  name: string;
  note: string;
  color: PaletteColor;
  ownerId: string;
  ownerName: string;
  clipCount: number;
  previewAssetIds: string[];
  projectId: string | null;
};
export type BoardProject = {
  id: string;
  title: string;
  status: string;
  tagId: string | null;
  /** Null when there is no task, or its task is one this reader cannot see. */
  tagName: string | null;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  versionCount: number;
  current: {
    id: string;
    versionNumber: number;
    createdAt: string;
    createdBy: string;
    lastDecision: "APPROVE" | "REQUEST_CHANGES" | null;
  } | null;
  /** `sizeBytes` is a string: it is a bigint on the wire. */
  unfinishedDraft: { id: string; sizeBytes: string } | null;
  approver: { userId: string; name: string } | null;
};
export type Board = { tasks: BoardTask[]; projects: BoardProject[] };
export type UploadSessionState = {
  id: string;
  state: string;
  partCount: number;
  observedParts: { partNumber: number; sizeBytes: number }[];
  missingParts: number[];
  nextAction: string;
};

export type Member = {
  userId: string;
  displayName: string;
  roles: string[];
  isYou: boolean;
  /** Theirs on this trip: it draws their route and backs their avatar. */
  color: PaletteColor;
  /** Chosen by them, rather than the one their place in the join order gave them. */
  colorChosen: boolean;
};
export type Highlight = {
  id: string;
  assetId: string;
  startMs: number;
  endMs: number;
  label: string | null;
  createdBy: string;
};
export type AssetComment = {
  id: string;
  authorId: string;
  offsetMs: number | null;
  body: string;
  parentId: string | null;
  createdAt: string;
};
export type Notification = {
  id: string;
  kind:
    | "SYNC_NUDGE"
    | "TASK_CREATED"
    | "TASK_CHANGED"
    | "REVISION_PUBLISHED"
    | "REVIEW_APPROVED"
    | "REVIEW_CHANGES_REQUESTED";
  fromUserId: string;
  /** A nudge's typed note; for every other kind, the tag's name or project's title as it read when sent. */
  message: string | null;
  tagId: string | null;
  deliverableId: string | null;
  createdAt: string;
  read: boolean;
};
